import axios from './axiosConfig';

/**
 * Request location permission and get current position
 * @param {number} userId - Current user ID
 * @returns {Promise<Object>} - Location data or error
 */
export const requestLocationAccess = async (userId) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const successCallback = async (position) => {
      try {
        const { latitude, longitude, accuracy } = position.coords;
        
        console.log('📍 Location obtained:', { latitude, longitude, accuracy });
        
        // Send location data to server
        const response = await axios.post('/ind_approv/location', {
          latitude,
          longitude,
          accuracy,
          timestamp: new Date().toISOString(),
          userId
        });

        console.log('📍 Location sent to server:', response.data);

        resolve({
          success: true,
          data: {
            latitude,
            longitude,
            accuracy,
            timestamp: new Date().toISOString()
          },
          serverResponse: response.data
        });
      } catch (error) {
        console.error('❌ Error sending location to server:', error);
        reject(error);
      }
    };

    const errorCallback = (error) => {
      let errorMessage = 'Unknown error occurred';
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Location access denied by user';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information unavailable';
          break;
        case error.TIMEOUT:
          errorMessage = 'Location request timed out';
          break;
        default:
          errorMessage = error.message;
      }
      
      console.error('❌ Location error:', errorMessage);
      reject(new Error(errorMessage));
    };

    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // Increased timeout
      maximumAge: 300000 // 5 minutes
    };

    console.log('📍 Requesting location access...');
    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
  });
};

/**
 * Check if location permission is granted
 * @returns {Promise<boolean>} - True if permission is granted
 */
export const checkLocationPermission = () => {
  return new Promise((resolve) => {
    if (!navigator.permissions) {
      console.log('📍 Permissions API not supported');
      resolve(false);
      return;
    }

    navigator.permissions.query({ name: 'geolocation' })
      .then((permissionStatus) => {
        console.log('📍 Location permission status:', permissionStatus.state);
        resolve(permissionStatus.state === 'granted');
      })
      .catch((error) => {
        console.log('📍 Error checking permission:', error);
        resolve(false);
      });
  });
};

/**
 * Show location permission dialog with multiple attempts
 * @param {number} userId - Current user ID
 * @returns {Promise<Object>} - Result of location request
 */
export const showLocationDialog = async (userId) => {
  try {
    console.log('📍 Starting location permission request for user:', userId);
    
    const hasPermission = await checkLocationPermission();
    console.log('📍 Has permission:', hasPermission);
    
    if (hasPermission) {
      console.log('📍 Permission already granted, requesting location...');
      return await requestLocationAccess(userId);
    } else {
      // Show custom dialog to request permission
      console.log('📍 Showing location permission dialog...');
      const userConfirmed = window.confirm(
        '🔍 LOCATION ACCESS REQUIRED\n\n' +
        'This application needs your location for enhanced security tracking.\n\n' +
        'This helps us:\n' +
        '• Track user activities for audit purposes\n' +
        '• Provide better security monitoring\n' +
        '• Ensure compliance with security policies\n\n' +
        'Click OK to allow location access, or Cancel to skip.\n\n' +
        'Note: Your location data is used only for security tracking and is not shared with third parties.'
      );
      
      if (userConfirmed) {
        console.log('📍 User confirmed location access');
        return await requestLocationAccess(userId);
      } else {
        console.log('📍 User denied location access');
        return {
          success: false,
          message: 'Location access denied by user'
        };
      }
    }
  } catch (error) {
    console.error('❌ Location access error:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Force location request (for testing)
 * @param {number} userId - Current user ID
 * @returns {Promise<Object>} - Result of location request
 */
export const forceLocationRequest = async (userId) => {
  try {
    console.log('📍 Force requesting location for user:', userId);
    
    // Try to get location directly without permission check
    return await requestLocationAccess(userId);
  } catch (error) {
    console.error('❌ Force location request failed:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Get location coordinates as a formatted string
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {number} accuracy - Accuracy in meters
 * @returns {string} - Formatted location string
 */
export const formatLocation = (latitude, longitude, accuracy) => {
  if (!latitude || !longitude) return 'Location not available';
  
  const lat = latitude.toFixed(6);
  const lng = longitude.toFixed(6);
  const acc = accuracy ? ` (±${Math.round(accuracy)}m)` : '';
  
  return `${lat}, ${lng}${acc}`;
}; 